import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DxfViewer from "@/components/DxfViewer";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

const LEVEL_LABEL = {
  extracted: "Estratto — non ancora rivisto",
  corrected: "Corretto — in attesa di approvazione",
  approved: "Approvato — utilizzabile per il prezzo",
};

export default function CadJobPanel({ jobId, onApproved }) {
  const [job, setJob] = useState(null);
  const [entities, setEntities] = useState([]);
  const [viewer, setViewer] = useState({ bbox: null, anomalies: [], layers: [], units_confirmed: false });
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: j } = await api.get(`/cad-jobs/${jobId}`);
      setJob(j);
      const c = j.manual_corrections || {};
      setEdit({
        thickness: c.thickness ?? j.result_normalized?.thickness ?? "",
        cut_length: c.cut_length ?? j.result_normalized?.cut_length ?? "",
        pierce_count: c.pierce_count ?? j.result_normalized?.pierce_count ?? "",
        hole_count: c.hole_count ?? j.result_normalized?.hole_count ?? "",
        units: c.units ?? j.result_normalized?.units ?? "",
      });
      const { data: ent } = await api.get(`/cad-jobs/${jobId}/entities`);
      setEntities(ent.entities || []);
      setViewer({
        bbox: ent.bbox,
        anomalies: ent.anomalies || [],
        layers: ent.layers || [],
        units_confirmed: ent.units_confirmed,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { if (jobId) load(); }, [jobId]);

  if (!job) return null;
  const norm = job.result_normalized || {};
  const raw = norm.raw_provider_result || {};
  const level = job.approval_level || "extracted";

  const saveCorrections = async () => {
    setSaving(true);
    try {
      const payload = {};
      ["thickness", "cut_length", "pierce_count", "hole_count", "units"].forEach(k => {
        if (edit[k] !== "" && edit[k] !== null && edit[k] !== undefined) {
          payload[k] = ["pierce_count", "hole_count"].includes(k) ? parseInt(edit[k]) : (k === "units" ? edit[k] : parseFloat(edit[k]));
        }
      });
      await api.put(`/cad-jobs/${jobId}/corrections`, payload);
      toast.success("Correzioni salvate (livello: corrected)");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    if (!viewer.units_confirmed && !edit.units) {
      toast.error("Confermare l'unità prima di approvare (giallo: unità mancanti)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/cad-jobs/${jobId}/approve`);
      toast.success("Job approvato: RFQ precompilata dai valori approvati");
      if (onApproved) onApproved();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const stateColor = level === "approved" ? "green" : (level === "corrected" ? "yellow" : "yellow");
  const Icon = level === "approved" ? CheckCircle2 : (norm.errors?.length ? XCircle : AlertTriangle);

  return (
    <section className="border border-border bg-white" data-testid="cad-job-panel">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Analisi DXF</div>
          <div className="font-display font-bold text-sm">Job {jobId.slice(-8)} · provider {job.provider}@{job.provider_version}</div>
        </div>
        <div className={`flex items-center gap-2 px-2 py-1 border bg-reliability-${stateColor}`}>
          <Icon size={14} />
          <span className="text-[10px] uppercase tracking-widest font-medium">{LEVEL_LABEL[level]}</span>
        </div>
      </div>

      <div className="p-4">
        <DxfViewer entities={entities} bbox={norm.dimensions} anomalies={viewer.anomalies} layers={viewer.layers} />
      </div>

      <div className="border-t border-border p-4 grid lg:grid-cols-2 gap-4">
        <div>
          <div className="font-display font-bold text-sm mb-2">Metriche estratte (mm)</div>
          <table className="dense-table w-full">
            <tbody>
              {[
                ["Unità disegno", `${norm.units || "unknown"} ${viewer.units_confirmed ? "✓" : "⚠ conferma"}`],
                ["Fattore scala → mm", raw.scale_to_mm],
                ["Bounding box", norm.dimensions ? `${fmtNum(norm.dimensions.x, 2)} × ${fmtNum(norm.dimensions.y, 2)}` : "—"],
                ["Lunghezza taglio", norm.cut_length != null ? `${fmtNum(norm.cut_length, 2)} mm` : "—"],
                ["Profili totali", raw.profile_count],
                ["Chiusi / Aperti", `${raw.closed_profiles} / ${raw.open_profiles}`],
                ["Esterni / Interni", `${raw.external_profiles} / ${raw.internal_profiles}`],
                ["Fori", norm.hole_count],
                ["Sfondamenti (pierces)", norm.pierce_count],
                ["Area lorda (mm²)", raw.gross_area_mm2],
                ["Area netta (mm²)", raw.net_area_mm2],
                ["Tolleranza usata", `${raw.tolerance_mm} mm`],
                ["Confidenza", `${(norm.confidence * 100).toFixed(0)}%`],
                ["Tempo elaborazione", `${norm.processing_time_ms} ms`],
              ].map(([k, v], i) => (
                <tr key={i}>
                  <td className="text-muted-foreground uppercase text-[10px] tracking-widest">{k}</td>
                  <td className="font-mono text-right">{v ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <div className="font-display font-bold text-sm mb-2">Correzioni umane</div>
          <div className="text-[11px] text-muted-foreground mb-3">
            Modifica i valori se il DXF ha imprecisioni. Il salvataggio porta il job a stato <b>corrected</b>. Solo dopo <b>approve</b> i valori alimentano il PricingEngine.
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><Label>Spessore (mm)</Label><Input data-testid="corr-thickness" type="number" step="0.1" value={edit.thickness} onChange={(e) => setEdit({ ...edit, thickness: e.target.value })} /></div>
            <div><Label>Unità</Label><Input data-testid="corr-units" value={edit.units} onChange={(e) => setEdit({ ...edit, units: e.target.value })} placeholder="mm / inch" /></div>
            <div><Label>Lunghezza taglio (mm)</Label><Input data-testid="corr-cutlen" type="number" value={edit.cut_length} onChange={(e) => setEdit({ ...edit, cut_length: e.target.value })} /></div>
            <div><Label>Sfondamenti</Label><Input data-testid="corr-pierces" type="number" value={edit.pierce_count} onChange={(e) => setEdit({ ...edit, pierce_count: e.target.value })} /></div>
            <div><Label>Fori</Label><Input data-testid="corr-holes" type="number" value={edit.hole_count} onChange={(e) => setEdit({ ...edit, hole_count: e.target.value })} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={saveCorrections} disabled={saving} data-testid="btn-save-corrections">Salva correzioni</Button>
            <Button size="sm" onClick={approve} disabled={saving || level === "approved" || norm.errors?.length} data-testid="btn-approve-cad">
              {level === "approved" ? "Già approvato" : "Approva & precompila RFQ"}
            </Button>
          </div>
          {level === "approved" && (
            <div className="mt-3 text-[11px] bg-reliability-green px-2 py-1 border">
              <b>Approvato</b> — i valori sono stati copiati nei dati tecnici manuali dell'RFQ.
            </div>
          )}
          {norm.errors?.length > 0 && (
            <div className="mt-3 text-[11px] bg-reliability-red px-2 py-1 border">
              Errori del provider: {norm.errors.join(", ")}
            </div>
          )}
          {norm.warnings?.length > 0 && (
            <div className="mt-2 text-[11px] text-muted-foreground">
              <div className="uppercase tracking-widest text-[9px] mb-1">Warning</div>
              <ul className="list-disc pl-4">{norm.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
